/* global localStorage */
import axios, { AxiosError } from "axios";
import { getAuthContext } from "./authContext";
import {
  encryptPayload,
  getServerPublicKey,
  invalidateServerPublicKeyCache,
  normalizeEncryptedResponseData,
} from "./encryption";

let isRefreshing = false;
let refreshQueue: Array<(token: string | null) => void> = [];

const flushQueue = (token: string | null) => {
  refreshQueue.forEach((resolve) => resolve(token));
  refreshQueue = [];
};

const refreshAccessToken = async () => {
  const auth = getAuthContext();
  if (!auth.refreshToken) {
    return null;
  }
  const serverPublicKey = await getServerPublicKey(auth.baseUrl);
  const encrypted = await encryptPayload({ refreshToken: auth.refreshToken }, serverPublicKey);
  const response = await axios.post(`${auth.baseUrl}/api/v1/auth/refresh`, encrypted.envelope, {
    headers: {
      "Content-Type": "application/json",
      "JC-Client-PKey": encrypted.clientPublicKey,
    },
  });
  const normalizedResponse = await normalizeEncryptedResponseData(response.data);
  const token =
    normalizedResponse?.data?.token ||
    normalizedResponse?.data?.accessToken ||
    normalizedResponse?.accessToken ||
    normalizedResponse?.token;
  if (!token) {
    return null;
  }
  localStorage.setItem("_usrCtx_909x", token);
  return token as string;
};

export const apiClient = axios.create({
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use(async (config) => {
  const auth = getAuthContext();
  config.baseURL = `${auth.baseUrl}/api/v1`;
  config.headers.Authorization = `Bearer ${auth.accessToken}`;
  if (auth.userId) {
    config.headers["X-User-Id"] = auth.userId;
  }
  config.headers["X-Tenant-Id"] = auth.tenantId;

  const method = (config.method || "get").toLowerCase();
  const shouldEncryptBody = ["post", "put", "patch"].includes(method) && !!config.data;
  if (shouldEncryptBody && !config.url?.includes("/app/encryption/public-key")) {
    const serverPublicKey = await getServerPublicKey(auth.baseUrl);
    const encrypted = await encryptPayload(config.data, serverPublicKey);
    config.data = encrypted.envelope;
    config.headers["JC-Client-PKey"] = encrypted.clientPublicKey;
  }
  return config;
});

apiClient.interceptors.response.use(
  async (response) => {
    response.data = await normalizeEncryptedResponseData(response.data);
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;
    const status = error.response?.status;
    const message = (error.response?.data as any)?.message || "";

    // If decryption failed on the server, the cached server public key is stale
    // (likely from a redeploy or rotation). Drop the cache and retry once.
    if (status === 400 && /decrypt/i.test(message) && !originalRequest?._decryptRetry) {
      originalRequest._decryptRetry = true;
      const auth = getAuthContext();
      invalidateServerPublicKeyCache(auth.baseUrl);
      return apiClient(originalRequest);
    }

    if (status !== 401 || originalRequest?._retry) {
      throw error;
    }

    if (isRefreshing) {
      const token = await new Promise<string | null>((resolve) => {
        refreshQueue.push(resolve);
      });
      if (!token) {
        throw error;
      }
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return apiClient(originalRequest);
    }

    isRefreshing = true;
    originalRequest._retry = true;

    try {
      const token = await refreshAccessToken();
      flushQueue(token);
      if (!token) {
        throw error;
      }
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return apiClient(originalRequest);
    } finally {
      isRefreshing = false;
    }
  }
);
